package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.site.i18n.tf
import kotlin.js.Date
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * When the numbers above this line were read.
 *
 * **A meter without a timestamp invites the reader to trust it as live.** Usage and spend figures
 * are fetched once when the surface mounts and then sit there unchanged; somebody who left the tab
 * open over lunch is looking at an hour-old number with nothing on screen saying so, and the
 * decision they make from it — top up, change plan, chase a project's spend — is made against a
 * balance that has moved.
 *
 * Deliberately not a relative "2 minutes ago" that re-renders: a clock that updates itself is the
 * animated live value this product's own rules exclude, and it would make a stale figure look
 * freshly confirmed every time it ticked. A fixed reading time is the honest form.
 */
@Composable
fun LFreshness(readAt: Date, modifier: Modifier = Modifier) {
    Span(
        modifier
            .color(ColorMode.current.toSitePalette().textMuted)
            .fontSize(TypeScale.Micro)
            .toAttrs { attr("data-freshness", "") },
    ) {
        Text(tf("ds.ds.lfreshness.updated", "Updated {when}", "when" to formatReadingTime(readAt)))
    }
}

/**
 * The reading time as the reader's own locale writes a clock.
 *
 * Hours and minutes only. Seconds would imply a precision the fetch does not have, and a date would
 * be noise for a value that is almost always minutes old — the case this exists to catch is a tab
 * left open, which shows as a time that no longer matches the wall clock.
 */
internal fun formatReadingTime(readAt: Date): String {
    val hours = readAt.getHours().toString().padStart(2, '0')
    val minutes = readAt.getMinutes().toString().padStart(2, '0')
    return "$hours:$minutes"
}
